<template>
    <div class="max-w-screen-md space-y-4 py-2 md:py-4">
        <div v-if="dues !== null">
            <template v-if="isAdvanced">
                <span class="font-semibold">{{ duesLabel }}</span
                >&nbsp;&nbsp;<span class="text-lg"
                    >{{ dues }}&nbsp;{{
                        t('billing.advanced', {
                            cycle: props.billingCycleString
                        })
                    }}</span
                >
                <br />
                <div class="text-xs italic">
                    {{ t('billing.duesDisclaimer') }}
                </div>
            </template>
            <template v-else>
                <span class="font-semibold">{{ duesLabel }}</span
                >&nbsp;&nbsp;<span class="text-lg">{{ dues }}</span>
                <br />
                <div class="text-xs italic">
                    {{ t('billing.duesDisclaimer') }}
                </div>
            </template>
        </div>

        <div v-if="formattedNextBillDate !== null">
            <span class="font-semibold">{{ t('billing.nextBillDate') }}</span
            >&nbsp;&nbsp;<span class="text-lg">{{
                formattedNextBillDate
            }}</span>
        </div>
    </div>
</template>

<script lang="ts">
import { graphql } from '~/resources/graphql'

export const DuesPricingDisplayDuesPricingPreviewItemFragment = graphql(
    /** GraphQL */ `
        fragment DuesPricingDisplayDuesPricingPreviewItemFragment on DuesPricingPreviewItem {
            quantity
            price
        }
    `
)
</script>

<script setup lang="ts">
import { computed } from 'vue'
import { Decimal } from '~/composables/support/decimal'
import type {
    BillingInterval,
    DuesPricingDisplayDuesPricingPreviewItemFragmentFragment
} from '~/resources/graphql/graphql'
import {
    formatTimelessDate,
    parseTimeless
} from '~/composables/support/localization'
import { useI18n } from 'vue-i18n'

const props = defineProps<{
    duesPricing:
        | DuesPricingDisplayDuesPricingPreviewItemFragmentFragment[]
        | null
        | undefined
    billingInterval: BillingInterval
    billingCycleString: string
    nextBillDate: string | null | undefined
}>()

const { t } = useI18n({ useScope: 'global' })

const dues = computed(() => {
    if (props.duesPricing === undefined || props.duesPricing === null) {
        return null
    }

    let total = Decimal.zero()
    props.duesPricing.forEach(item => {
        if (item.price !== null) {
            const quantity = new Decimal(item.quantity.toString())
            total = total.plus(new Decimal(item.price).multipliedBy(quantity))
        }
    })
    return total.formatCurrency()
})

const duesLabel = computed(() => {
    if (props.billingInterval === 'WEEKLY') {
        return t('billing.weeklyDues')
    } else if (props.billingInterval === 'FORTNIGHTLY') {
        return t('billing.fortnightlyDues')
    } else if (props.billingInterval === 'MONTHLY') {
        return t('billing.monthlyDues')
    } else if (props.billingInterval === 'QUARTERLY') {
        return t('billing.quarterlyDues')
    } else if (props.billingInterval === 'YEARLY') {
        return t('billing.yearlyDues')
    } else if (props.billingInterval === 'DAILY') {
        return t('billing.dailyDues')
    } else if (props.billingInterval === 'ADVANCED') {
        return t('billing.dues')
    } else {
        throw new Error()
    }
})

const isAdvanced = computed(() => props.billingInterval === 'ADVANCED')

const formattedNextBillDate = computed(() => {
    if (props.nextBillDate === null || props.nextBillDate === undefined) {
        return null
    }
    return formatTimelessDate(parseTimeless(props.nextBillDate))
})
</script>
